import { PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '@/components/ui';
import {
  ISO_WEEKDAYS,
  createAvailabilitySlot,
  weeklyAvailabilityPreview,
} from '../utils/availabilityPolicy';

export default function AvailabilityPolicyEditor({
  value,
  onChange,
  consequenceTypes = [],
}) {
  const policy = value;
  const automatic = policy.autoReschedule || {
    enabled: false,
    maxAttempts: 1,
    escalationTypeId: null,
  };
  const updatePolicy = updates => onChange({ ...policy, ...updates });
  const updateAutomatic = updates => updatePolicy({
    autoReschedule: {...automatic, ...updates},
  });
  const updateSlot = (index, updates) => {
    const slots = policy.slots.map((slot, slotIndex) =>
      slotIndex === index ? { ...slot, ...updates } : slot
    );
    updatePolicy({ slots });
  };
  const moveSlot = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= policy.slots.length) return;
    const slots = [...policy.slots];
    [slots[index], slots[target]] = [slots[target], slots[index]];
    updatePolicy({ slots });
  };
  const preview = weeklyAvailabilityPreview(policy);

  return (
    <section className="rounded-xl border border-base-300 bg-base-200/40 p-4 space-y-4" data-testid="availability-policy-editor">
      <label className="flex cursor-pointer items-start justify-between gap-4">
        <div>
          <div className="font-semibold">Session availability</div>
          <div className="text-sm text-base-content/65">
            Automatically assign the next eligible session without changing the Queue workflow.
          </div>
        </div>
        <input
          type="checkbox"
          className="toggle toggle-primary"
          checked={policy.enabled === true}
          onChange={event => updatePolicy({ enabled: event.target.checked })}
          aria-label="Enable session availability"
        />
      </label>

      {policy.enabled && (
        <>
          <div className="rounded-lg bg-base-100 px-3 py-2 text-sm">
            Times use <span className="font-semibold">{policy.timezone}</span>. Assignment closes at the session start unless you set an earlier cutoff.
          </div>

          <div className="space-y-3">
            {policy.slots.map((slot, index) => (
              <div key={slot.id} className="rounded-lg border border-base-300 bg-base-100 p-3 space-y-3" data-testid={`availability-slot-${index}`}>
                <div className="flex items-center gap-2">
                  <Input
                    value={slot.label}
                    onChange={event => updateSlot(index, { label: event.target.value })}
                    placeholder="e.g. Morning session"
                    aria-label={`Session ${index + 1} label`}
                    className="input-bordered flex-1"
                  />
                  <Button type="button" className="btn-ghost btn-sm btn-square" onClick={() => moveSlot(index, -1)} disabled={index === 0} aria-label="Move session up">
                    <ChevronUpIcon className="h-4 w-4" />
                  </Button>
                  <Button type="button" className="btn-ghost btn-sm btn-square" onClick={() => moveSlot(index, 1)} disabled={index === policy.slots.length - 1} aria-label="Move session down">
                    <ChevronDownIcon className="h-4 w-4" />
                  </Button>
                  <Button type="button" className="btn-ghost btn-sm btn-square text-error" onClick={() => updatePolicy({ slots: policy.slots.filter((_, slotIndex) => slotIndex !== index) })} aria-label="Delete session">
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2" aria-label={`Session ${index + 1} weekdays`}>
                  {ISO_WEEKDAYS.map(day => {
                    const selected = slot.weekdays.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        className={`btn btn-xs ${selected ? 'btn-primary' : 'btn-outline'}`}
                        aria-pressed={selected}
                        onClick={() => updateSlot(index, {
                          weekdays: selected
                            ? slot.weekdays.filter(value => value !== day.value)
                            : [...slot.weekdays, day.value].sort((a, b) => a - b),
                        })}
                      >
                        {day.short}
                      </button>
                    );
                  })}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="form-control">
                    <span className="label-text mb-1">Starts</span>
                    <Input type="time" value={slot.startTime} onChange={event => updateSlot(index, { startTime: event.target.value })} className="input-bordered" />
                  </label>
                  <label className="form-control">
                    <span className="label-text mb-1">Ends</span>
                    <Input type="time" value={slot.endTime} onChange={event => updateSlot(index, { endTime: event.target.value })} className="input-bordered" />
                  </label>
                  <label className="form-control">
                    <span className="label-text mb-1">Assignment closes (minutes before)</span>
                    <Input type="number" min="0" step="1" value={slot.assignmentClosesMinutesBeforeStart ?? 0} onChange={event => updateSlot(index, { assignmentClosesMinutesBeforeStart: event.target.value })} className="input-bordered" />
                  </label>
                  <label className="form-control">
                    <span className="label-text mb-1">Location (optional)</span>
                    <Input value={slot.location || ''} onChange={event => updateSlot(index, { location: event.target.value })} placeholder="e.g. Room 12" className="input-bordered" />
                  </label>
                </div>

                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input type="checkbox" className="checkbox checkbox-sm" checked={slot.active !== false} onChange={event => updateSlot(index, { active: event.target.checked })} />
                  Session active
                </label>
              </div>
            ))}
          </div>

          <Button type="button" className="btn-outline btn-sm" onClick={() => updatePolicy({ slots: [...policy.slots, createAvailabilitySlot(policy.slots.length)] })}>
            <PlusIcon className="h-4 w-4" /> Add session
          </Button>

          <div className="rounded-lg border border-base-300 bg-base-100 p-3 space-y-3">
            <label className="flex cursor-pointer items-start justify-between gap-4">
              <div>
                <div className="font-semibold">After a recorded no-show</div>
                <div className="text-sm text-base-content/65">
                  Move the learner to the next configured session only after
                  staff explicitly record a missed session. Time passing alone
                  never triggers this.
                </div>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-primary"
                checked={automatic.enabled === true}
                onChange={event => updateAutomatic({
                  enabled: event.target.checked,
                })}
                aria-label="Enable automatic rescheduling after a recorded no-show"
              />
            </label>

            {automatic.enabled && (
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="form-control">
                  <span className="label-text mb-1">
                    Automatic reschedule attempts
                  </span>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    step="1"
                    value={automatic.maxAttempts ?? 1}
                    onChange={event => updateAutomatic({
                      maxAttempts: Number(event.target.value),
                    })}
                    className="input-bordered"
                  />
                  <span className="label-text-alt mt-1">
                    After this limit, the item stays in follow-up for staff.
                  </span>
                </label>

                <label className="form-control">
                  <span className="label-text mb-1">
                    Suggested escalation (optional)
                  </span>
                  <select
                    value={automatic.escalationTypeId || ''}
                    onChange={event => updateAutomatic({
                      escalationTypeId: event.target.value || null,
                    })}
                    className="select select-bordered w-full"
                  >
                    <option value="">Staff decides in follow-up</option>
                    {consequenceTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.singular || type.label || type.value}
                      </option>
                    ))}
                  </select>
                  <span className="label-text-alt mt-1">
                    Swan suggests this type; it does not create it silently.
                  </span>
                </label>
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 text-sm font-semibold">Weekly preview</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {preview.map(day => (
                <div key={day.value} className="flex min-h-9 items-start gap-2 rounded-lg bg-base-100 px-3 py-2 text-sm">
                  <span className="w-9 shrink-0 font-semibold">{day.short}</span>
                  <span className="text-base-content/70">
                    {day.slots.length
                      ? day.slots.map(slot => `${slot.startTime}–${slot.endTime} ${slot.label}`).join(' · ')
                      : 'No sessions'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
